import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useGrows } from "@/lib/api/grows";
import { useRefillSessions } from "@/lib/api/refill-sessions";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorState } from "@/components/error-state";

// Refills list — filterable by grow. Tracking the active session start /
// close / void chrome lives on the detail page; this list just shows
// what's there.

// Radix Select reserves the empty string for "no value / show placeholder",
// so the "all grows" option uses a sentinel that the API hook converts
// back to undefined before it hits the wire.
const ALL_GROWS = "__all__";

export function RefillsListPage() {
  const grows = useGrows();
  const [growId, setGrowId] = useState<string>(ALL_GROWS);
  const [includeVoided, setIncludeVoided] = useState(false);
  const sessions = useRefillSessions({
    grow_id: growId === ALL_GROWS ? undefined : growId,
    include_voided: includeVoided,
  });

  // Resolve each session's grow_id to a readable plant, so rows say what the
  // grow is instead of only a date + truncated id.
  const growById = new Map((grows.data?.items ?? []).map((g) => [g.id, g]));

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Refills</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeVoided}
              onChange={(e) => setIncludeVoided(e.target.checked)}
            />
            include voided
          </label>
          <Link to="/refills/new">
            <Button size="sm">Start refill</Button>
          </Link>
          <div className="min-w-[14rem]">
            <Select value={growId} onValueChange={setGrowId}>
              <SelectTrigger>
                <SelectValue placeholder="all grows" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_GROWS}>all grows</SelectItem>
                {grows.data?.items.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.species}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {sessions.error && (
        <ErrorState error={sessions.error} retry={sessions.refetch} />
      )}
      {sessions.isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      <ul className="divide-y rounded-md border">
        {sessions.data?.items.map((s) => {
          const g = growById.get(s.grow_id);
          return (
            <li key={s.id}>
              <Link
                to="/refills/$sessionId"
                params={{ sessionId: s.id }}
                className="block px-4 py-3 hover:bg-accent/50"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div className="space-y-0.5">
                    <p className="font-medium">
                      {g ? (
                        g.species
                      ) : (
                        <>
                          grow{" "}
                          <code className="text-xs">{s.grow_id.slice(-6)}</code>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(s.started_at)}
                      {g &&
                        ` · ${g.system_type} · ${g.count} plant${g.count === 1 ? "" : "s"}`}
                      {` · ${s.trigger}`}
                      {s.decision_type && ` · ${s.decision_type}`}
                      {s.closed_at && " · closed"}
                      {s.voided_at && " · voided"}
                    </p>
                  </div>
                  {s.recipe_id && (
                    <p className="text-xs text-muted-foreground">
                      recipe v{s.recipe_version}
                    </p>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}
